import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useState } from 'react';

// third-party
import { Chance } from 'chance';
import jwtDecode from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import axios, { setOrgIdInterceptor } from 'utils/axios';
import logger, { initClientLogger, resetClientLogger, setClientLoggerToken } from 'utils/logger';

const chance = new Chance();

// constant
const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    userId: 0,
    email: null,
    clinicGrants: {},
    organizationMeta: {},
    organizations: [],
    userMetadata: {
        isProviderAccount: false,
        providerId: null,
        firstName: '',
        lastName: ''
    }
};

const verifyToken = (serviceToken) => {
    if (!serviceToken) {
        return false;
    }
    const decoded = jwtDecode(serviceToken);
    /**
     * Property 'exp' does not exist on type '<T = unknown>(token, options?: JwtDecodeOptions | undefined) => T'.
     */
    return decoded.exp > Date.now() / 1000;
};

// Support-impersonation sessions carry an `impersonation` claim minted by the
// server's support-login endpoint: { actor: <platform admin email>,
// platformAdminUserId }. Absent (null) on normal sessions.
const getImpersonationFromToken = (serviceToken) => {
    if (!serviceToken) {
        return null;
    }
    try {
        const decoded = jwtDecode(serviceToken);
        return decoded.impersonation || null;
    } catch (e) {
        return null;
    }
};

const configureOrgIdInterceptor = (orgId) => {
    setOrgIdInterceptor(orgId);
    initClientLogger(orgId);
};

const setSession = (serviceToken) => {
    if (serviceToken) {
        const bearer = `Bearer ${serviceToken}`;
        localStorage.setItem('serviceToken', serviceToken);
        axios.defaults.headers.common.Authorization = bearer;
        setClientLoggerToken(bearer);
    } else {
        localStorage.removeItem('serviceToken');
        delete axios.defaults.headers.common.Authorization;
        setClientLoggerToken(null);
    }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);
    // Kept outside accountReducer: derived from the token, not the /me payload.
    // Set on both entry paths (init after a refresh, loginWithSupportCode).
    const [impersonation, setImpersonation] = useState(null);
    // Held login response for a multi-org account awaiting its org choice.
    // Deliberately React state only — nothing is persisted (no setSession, no
    // LOGIN dispatch) until an org is chosen, so GuestGuard doesn't redirect
    // and an abandoned tab leaves no session behind.
    const [pendingOrgSelection, setPendingOrgSelection] = useState(null);

    // Arms the org interceptor and publishes an account-shaped payload
    // (login/support-login/org-pick response, or the /me body) into the
    // reducer — the one place that maps a server payload to account state.
    const publishAccount = (data) => {
        configureOrgIdInterceptor(data.organizationMeta.meta.id);
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                email: data.email,
                userId: data.userId,
                clinicGrants: data.clinicGrants,
                organizationMeta: data.organizationMeta,
                organizations: data.organizations || [],
                userMetadata: data.userMetadata
            }
        });
    };

    // Shared completion for every login-shaped payload (login, support login,
    // org pick): persists the session, then publishes the account state.
    const completeLogin = (data) => {
        setSession(data.serviceToken);
        setPendingOrgSelection(null);
        publishAccount(data);
    };

    const loadAccount = async () => {
        const response = await axios.get('/api/account/me');
        publishAccount(response.data);
    };

    useEffect(() => {
        const init = async () => {
            try {
                const serviceToken = window.localStorage.getItem('serviceToken');
                if (serviceToken && verifyToken(serviceToken)) {
                    setSession(serviceToken);
                    setImpersonation(getImpersonationFromToken(serviceToken));
                    await loadAccount();
                } else {
                    setImpersonation(null);
                    dispatch({
                        type: LOGOUT
                    });
                }
            } catch (err) {
                logger.error('account:init failed', { error: err?.message, code: err?.code });
                setImpersonation(null);
                dispatch({
                    type: LOGOUT
                });
            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Shared by every identity provider: posts the provider's ID token to its
    // login route and either completes the login or holds it for the org
    // picker. The response shape is the same for every provider.
    const loginWithProviderCredential = async (path, credential) => {
        const response = await axios.post(path, { credential });
        const organizations = response.data.organizations || [];
        if (organizations.length > 1) {
            // Multi-org account: hold the response and let the login view
            // render the org picker. The session (minted against the default
            // org) is not persisted until an org is chosen.
            setPendingOrgSelection({ organizations, response: response.data });
            return;
        }
        completeLogin(response.data);
    };

    const loginWithGoogle = async (credential) => {
        await loginWithProviderCredential('/api/account/login-google', credential);
    };

    const loginWithMicrosoft = async (credential) => {
        await loginWithProviderCredential('/api/account/login-microsoft', credential);
    };

    // Completes a held multi-org login for the chosen org. Picking the
    // default org completes with the held response; any other org exchanges
    // the held token via switch-org first.
    const chooseLoginOrg = async (orgId) => {
        if (!pendingOrgSelection) {
            logger.warn('account:chooseLoginOrg called without a pending selection', { orgId });
            return;
        }
        const heldResponse = pendingOrgSelection.response;
        if (orgId === heldResponse.organizationMeta.meta.id) {
            completeLogin(heldResponse);
            return;
        }
        // Explicit Authorization header: the held token was never passed to
        // setSession, so the axios default is not armed yet.
        const response = await axios.post(
            '/api/account/switch-org',
            { orgId },
            { headers: { Authorization: `Bearer ${heldResponse.serviceToken}` } }
        );
        completeLogin(response.data);
    };

    const cancelLoginOrgSelection = () => {
        setPendingOrgSelection(null);
    };

    const logout = () => {
        setSession(null);
        setImpersonation(null);
        configureOrgIdInterceptor(0);
        resetClientLogger();
        dispatch({ type: LOGOUT });
    };

    // In-app org switch (profile menu): re-issues the session for the target
    // org, then does a full page reload so every org-scoped slice, the axios
    // interceptor, and the clinic-defaulting cascade re-boot from the new
    // token — the same path as a fresh page load.
    const switchOrg = async (orgId) => {
        let response;
        try {
            response = await axios.post('/api/account/switch-org', { orgId });
        } catch (err) {
            // A 401 means the presenting session is dead (expired mid-app) —
            // no request can succeed anymore, so fall back to a clean login
            // instead of leaving a switch entry that silently does nothing.
            if (err?.statusCode === 401) {
                logout();
                return;
            }
            throw err;
        }
        setSession(response.data.serviceToken);
        window.location.assign('/');
    };

    // Entry for platform-staff support impersonation: exchanges the one-time
    // code from the platform-admin handoff URL for a short-lived support-user
    // session (the response is login-shaped, always single-org).
    const loginWithSupportCode = async (code) => {
        const response = await axios.post('/api/account/support-login', { code });
        setImpersonation(getImpersonationFromToken(response.data.serviceToken));
        completeLogin(response.data);
    };

    const register = async (email, password, firstName, lastName) => {
        // todo: this flow need to be recode as it not verified
        const id = chance.bb_pin();
        const response = await axios.post('/api/account/register', {
            id,
            email,
            password,
            firstName,
            lastName
        });
        let users = response.data;

        if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
            const localUsers = window.localStorage.getItem('users');
            users = [
                ...JSON.parse(localUsers),
                {
                    id,
                    email,
                    password,
                    name: `${firstName} ${lastName}`
                }
            ];
        }

        window.localStorage.setItem('users', JSON.stringify(users));
    };

    const refreshAccount = async () => {
        const serviceToken = window.localStorage.getItem('serviceToken');
        // Same expiry check init() runs at load, applied mid-session: only logs out on a
        // missing/dead token, and callers gate this to neutral pages, so no work is lost.
        if (!serviceToken || !verifyToken(serviceToken)) {
            logout();
            return;
        }
        try {
            await loadAccount();
        } catch (err) {
            // Transient failure: keep current grants. The caller already advanced its staleness
            // clock, so this waits out the window rather than retrying on a flaky network.
            logger.warn('account:refresh failed', { error: err?.message, code: err?.code });
        }
    };

    const resetPassword = (email) => console.log(email);

    const updateProfile = () => {};

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    return (
        <JWTContext.Provider
            value={{
                ...state,
                impersonation,
                pendingOrgSelection,
                chooseLoginOrg,
                cancelLoginOrgSelection,
                switchOrg,
                loginWithGoogle,
                loginWithMicrosoft,
                loginWithSupportCode,
                logout,
                register,
                resetPassword,
                updateProfile,
                refreshAccount
            }}
        >
            {children}
        </JWTContext.Provider>
    );
};

JWTProvider.propTypes = {
    children: PropTypes.node
};

export default JWTContext;
